import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AppShell } from "@/components/AppShell";
import { MasterSelect } from "@/components/MasterSelect";
import { api, formatApiErrorDetail } from "@/api";
import { toast } from "sonner";
import { Printer, Save } from "lucide-react";
import { ProfitPanel } from "@/components/ProfitPanel";
import { toDMY } from "@/lib/dates";

const L = ({ children }) => (
  <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">{children}</Label>
);

const rs = (v) => `₹${Number(v || 0).toFixed(2)}`;

export default function CustomerDashboard() {
  const [params, setParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(params.get("name") || "");
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ phone: "", address: "", gstin: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/masters/customer").then(({ data }) => setCustomers(data.map((c) => c.name))).catch(() => {});
  }, []);

  useEffect(() => {
    if (!customer) return setData(null);
    setParams(customer ? { name: customer } : {});
    api
      .get(`/customers/${encodeURIComponent(customer)}/profile`)
      .then(({ data }) => {
        setData(data);
        setForm({ phone: data.phone, address: data.address, gstin: data.gstin, notes: data.notes });
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/customers/${encodeURIComponent(customer)}/profile`, form);
      setData(data);
      toast.success("Customer details saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const printBill = async (id) => {
    try {
      const res = await api.get(`/transactions/${id}/invoice`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Could not open the bill");
    }
  };

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--mod-primary)]">Customers</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">Customer Dashboard</h1>
        </div>

        <div className="mt-6 max-w-md">
          <L>Customer</L>
          <MasterSelect
            testId="customer-select"
            options={customers}
            value={customer}
            onChange={setCustomer}
            placeholder="Select customer"
            emptyHint="Add customers in Admin → Customer Master"
          />
        </div>

        {!data ? (
          <div data-testid="customer-empty-state" className="mt-8 border border-dashed border-zinc-300 p-12 text-center">
            <h3 className="font-head text-xl font-bold tracking-tight">Pick a customer</h3>
            <p className="mt-1 text-sm text-zinc-500">Their business, outstanding, bills and your notes appear here.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="customer-stats">
              {[
                ["Bills", data.stats.bills],
                ["Total business", rs(data.stats.business)],
                ["Discount given", rs(data.stats.discount_given)],
                ["Outstanding", rs(data.stats.outstanding)],
                ["Last bill", data.stats.last_bill_date || "-"],
              ].map(([label, value]) => (
                <div key={label} className="border border-zinc-200 bg-white p-4">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</p>
                  <p className="font-mono-num mt-1.5 text-lg font-semibold">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
              <div className="border border-zinc-200 bg-white p-5">
                <h2 className="font-head text-lg font-bold uppercase tracking-tight">Details & notes</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <L>Phone</L>
                    <Input data-testid="customer-phone-input" className="mt-2 rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div>
                    <L>GSTIN</L>
                    <Input data-testid="customer-gstin-input" className="mt-2 rounded-sm" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <L>Address</L>
                    <Input data-testid="customer-address-input" className="mt-2 rounded-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <L>Notes about this customer</L>
                    <Textarea
                      data-testid="customer-notes-input"
                      rows={6}
                      className="mt-2 rounded-sm"
                      placeholder="Payment habits, preferred qualities, rate talk, anything you want to remember"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                </div>
                <Button data-testid="customer-save-button" className="mt-4 rounded-sm bg-[var(--mod-primary)] text-white hover:opacity-90" disabled={saving} onClick={save}>
                  <Save size={15} className="mr-2" /> {saving ? "Saving…" : "Save details"}
                </Button>

                <div className="mt-6 border-t border-dashed border-zinc-200 pt-4" data-testid="customer-more-details">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">More details</p>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-zinc-600">
                    {[
                      ["Contact person", data.contact_person],
                      ["Alternate phone", data.alt_phone],
                      ["Email", data.email],
                      ["City / State", [data.city, data.state].filter(Boolean).join(", ")],
                      ["Pincode", data.pincode],
                      ["Transport", data.transport],
                      ["Credit days", data.credit_days || 0],
                      ["Credit limit", `₹${Number(data.credit_limit || 0).toLocaleString("en-IN")}`],
                      ["Opening balance", `₹${Number(data.opening_balance || 0).toLocaleString("en-IN")}`],
                    ].map(([label, value]) => (
                      <p key={label}>
                        <span className="text-zinc-400">{label}: </span>
                        <span className="font-mono-num">{value || "-"}</span>
                      </p>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-zinc-400">
                    Edit these in Admin → Customer Master → Details
                  </p>
                </div>

                {data.company_discounts?.length > 0 && (
                  <div className="mt-6 border-t border-dashed border-zinc-200 pt-4" data-testid="customer-saved-rates">
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Saved hisab rates</p>
                    {data.company_discounts.map((r) => (
                      <p key={r.company} className="font-mono-num mt-1 text-xs text-zinc-600">
                        {r.company} — {r.pct}%
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-zinc-200 bg-white p-5">
                <h2 className="font-head text-lg font-bold uppercase tracking-tight">Recent bills</h2>
                {data.recent_bills.length === 0 ? (
                  <p className="mt-3 text-sm text-zinc-500">No bills yet.</p>
                ) : (
                  <div className="mt-3 divide-y divide-zinc-100" data-testid="customer-recent-bills">
                    {data.recent_bills.map((b) => (
                      <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                        <span className="text-sm">
                          <span className="font-semibold">{b.ref_no}</span>
                          <span className="text-zinc-500"> · {toDMY(b.date)}</span>
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="font-mono-num text-sm">{rs(b.grand_total)}</span>
                          <Button
                            data-testid={`customer-print-${b.ref_no}`}
                            size="sm"
                            variant="outline"
                            className="rounded-sm"
                            onClick={() => printBill(b.id)}
                          >
                            <Printer size={13} className="mr-1.5" /> Bill
                          </Button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <ProfitPanel profit={data.profit} title={`Profitability — ${customer}`} />
          </>
        )}
      </main>
    </AppShell>
  );
}
